import { clearSession, getAccessToken } from './cognito';
import { isAdminView } from './adminView';
import { getSupportStaffViewToken } from './supportStaffView';
import { clearPortalSession, isPortalSession } from './portalSession';
import { BILLING_ORIGIN, hasCustomerSession, isSupportHost, markCustomerSession, portalHeaders, safeReturnPath } from './portalPlatform';
import i18n from '../i18n';

const API = import.meta.env.VITE_API_BASE ?? '/api';
let restoring: Promise<boolean> | null = null;
let restoreInFlight = false;
export async function establishCustomerSession(): Promise<boolean> {
  const token = await getAccessToken();
  const headers = new Headers(portalHeaders());
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(`${API}/customer-session`, { method: 'POST', headers, credentials: 'include' });
  if (!res.ok) return false;
  markCustomerSession(true);
  return true;
}
export function restoreCustomerSession(force = false): Promise<boolean> {
  if (restoring && (!force || restoreInFlight)) return restoring;
  restoreInFlight = true;
  restoring = (async () => {
    if (isAdminView()||getSupportStaffViewToken()) return true;
    const res = await fetch(`${API}/public/portal/session`, { credentials: 'include', cache: 'no-store' });
    if (!res.ok) throw new Error('Could not check your Future Demand session.');
    const result = await res.json() as { authenticated: boolean; reason?: string };
    if (result.authenticated) { markCustomerSession(true); return true; }
    const hadSession = hasCustomerSession();
    markCustomerSession(false);
    if (result.reason === 'session_revoked' || result.reason === 'invalid_session') {
      clearSession(); clearPortalSession();
      await fetch(`${API}/public/portal/logout`, { method: 'POST', headers: portalHeaders(), credentials: 'include' });
      return false;
    }
    // Cognito tokens/refresh stay on Billing. Support renews with a top-level,
    // PKCE-bound handoff so browser third-party-cookie policies do not matter.
    if (!isSupportHost() && (await getAccessToken() || isPortalSession())) return establishCustomerSession();
    if (hadSession) clearPortalSession();
    return false;
  })().finally(() => { restoreInFlight = false; });
  return restoring;
}
function random(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join('');
}
export async function sha256(value: string): Promise<string> {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('');
}
export async function startPortalSignIn(returnTo = window.location.pathname + window.location.search): Promise<void> {
  if (!isSupportHost()) {
    window.location.assign(`/login?returnTo=${encodeURIComponent(safeReturnPath(returnTo))}`);
    return;
  }
  const verifier = random(); const state = random();
  // Keyed by state so tabs do not overwrite one another; localStorage permits an
  // email link opened in a new tab of this browser. These are one-use PKCE secrets,
  // never access tokens, sessions, or magic-link credentials.
  for (const key of Object.keys(localStorage).filter(k => k.startsWith('fd_handoff.'))) {
    try { if (Date.now() - JSON.parse(localStorage.getItem(key) ?? '{}').created_at > 45 * 60_000) localStorage.removeItem(key); }
    catch { localStorage.removeItem(key); }
  }
  localStorage.setItem(`fd_handoff.${state}`, JSON.stringify({ verifier, created_at: Date.now() }));
  const url = new URL('/auth/portal', BILLING_ORIGIN);
  url.search = new URLSearchParams({ destination: window.location.origin, challenge: await sha256(verifier), state,
    return_to: safeReturnPath(returnTo, '/support'), language: i18n.resolvedLanguage === 'de' ? 'de' : 'en' }).toString();
  window.location.assign(url.toString());
}
let completing: Promise<{ return_to: string; language: string }> | null = null;
export function completePortalSignIn(): Promise<{ return_to: string; language: string }> {
  if (completing) return completing;
  completing = (async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code'); const state = params.get('state');
    window.history.replaceState(null, '', '/auth/portal/complete');
    if (!state || !/^[a-f0-9]{64}$/.test(state)) throw new Error('invalid_handoff');
    const key = `fd_handoff.${state}`;
    const saved = JSON.parse(localStorage.getItem(key) ?? '{}') as { verifier?: string; created_at?: number };
    if (!saved.verifier || !saved.created_at || Date.now() - saved.created_at > 45 * 60_000) throw new Error('invalid_handoff');
    const res = await fetch(`${API}/public/portal/session/complete`, { method: 'POST', credentials: 'include', headers: portalHeaders(),
      body: JSON.stringify({ code, state, verifier: saved.verifier }) });
    if (!res.ok) throw new Error('invalid_handoff');
    localStorage.removeItem(key);
    markCustomerSession(true);
    return res.json() as Promise<{ return_to: string; language: string }>;
  })();
  return completing;
}
